import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import type { Section } from '@omni/ai/features/card.schema';

/** A `chart` section's items — `{label, value, value2, display}` unifies all chart variants. */
type ChartItem = Extract<Section, { type: 'chart' }>['items'][number];

/** One bar row with a computed fill percentage. */
interface BarRow {
  item: ChartItem;
  pct: number;
}

/** One point in a sparkline, with x/y pixel coords. */
interface SparkPoint {
  x: number;
  y: number;
  label: string;
  value: number;
}

/** One row in a grouped two-series bar chart. */
interface GroupedRow {
  item: ChartItem;
  pct1: number;
  pct2: number;
}

/** One row in a diverging signed-bar chart. */
interface DivergingRow {
  item: ChartItem;
  /** 0–100, for the positive side. */
  pctPos: number;
  /** 0–100, for the negative side. */
  pctNeg: number;
  positive: boolean;
  display: string;
}

/** One row in a bullet (actual-vs-target) chart. */
interface BulletRow {
  item: ChartItem;
  /** Actual bar fill, 0–100 of the shared scale. */
  pct: number;
  /** Target marker position, 0–100 of the shared scale; null when no target. */
  targetPct: number | null;
  /** Attainment vs target, e.g. "87%"; null when no target. */
  attainment: string | null;
  /** True when actual ≥ target. */
  met: boolean;
}

/** One arc segment in the donut chart. */
interface DonutSegment {
  item: ChartItem;
  pct: number;
  /** stroke-dasharray value for the full circumference */
  dasharray: string;
  /** stroke-dashoffset that positions this arc after preceding segments */
  dashoffset: string;
  color: string;
}

// Sparkline internal coordinate space — SVG is stretched via CSS width:100%.
const SPARK_W = 300;
const SPARK_H = 56;
const SPARK_PAD = 4;

// Donut geometry (matches card-progress ring: r=30 in a 80×80 viewBox).
const DONUT_R = 30;
const DONUT_CX = 40;
const DONUT_CY = 40;
const DONUT_CIRC = 2 * Math.PI * DONUT_R; // ≈ 188.5

/** Small accent-color palette for donut segments (CSS variable references + fallbacks). */
const DONUT_PALETTE = [
  'var(--nova-accent)',
  'var(--nova-accent-secondary)',
  'var(--nova-donut-3, #7c6aff)',
  'var(--nova-donut-4, #f59e42)',
  'var(--nova-donut-5, #22c55e)',
  'var(--nova-donut-6, #e879f9)',
];

/**
 * `chart` section, six variants:
 *  - `bars`     — ranked horizontal bars (label / track / value).
 *  - `trend`    — SVG sparkline over ordered periods; a 2nd series (value2)
 *                 adds a dashed comparison line on a shared axis.
 *  - `grouped`  — two-series horizontal bars with a legend.
 *  - `diverging`— signed bars from a center axis (top movers).
 *  - `donut`    — share/composition donut via inline SVG arcs.
 *  - `bullet`   — actual bar against a target marker (attainment).
 */
@Component({
  selector: 'card-chart',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './card-chart.component.html',
  styleUrls: ['./card-chart.component.scss'],
})
export class CardChartComponent {
  @Input() variant: 'bars' | 'trend' | 'grouped' | 'diverging' | 'donut' | 'bullet' = 'bars';
  @Input() title: string | null = null;
  @Input() unit: string | null = null;
  @Input() items: ChartItem[] = [];
  /** Labels for each data series, e.g. ['TRx', 'NRx']. Used by the `grouped` legend. */
  @Input() seriesLabels: string[] | null = null;

  readonly #translate = inject(TranslateService);

  readonly sparkW = SPARK_W;
  readonly sparkH = SPARK_H;
  readonly donutR = DONUT_R;
  readonly donutCx = DONUT_CX;
  readonly donutCy = DONUT_CY;
  readonly donutViewBox = '0 0 80 80';

  // ---- bars helpers --------------------------------------------------------

  barRows(items: ChartItem[]): BarRow[] {
    const max = Math.max(...items.map(i => i.value), 1);
    return items.map(item => ({ item, pct: (item.value / max) * 100 }));
  }

  displayValue(item: ChartItem, unit: string | null): string {
    return item.display ?? `${item.value}${unit ? ' ' + unit : ''}`;
  }

  // ---- trend / sparkline helpers -------------------------------------------

  /** True when any point carries a 2nd trend series (value2) — a comparison line. */
  hasSeries2(items: ChartItem[]): boolean {
    return items.some(i => i.value2 != null);
  }

  /**
   * Shared y-range across BOTH series so the comparison line is read on the
   * same axis as the primary line. With no value2 present this collapses to the
   * primary series' own min/max — identical to a single-series sparkline.
   */
  private trendRange(items: ChartItem[]): { min: number; max: number } {
    const values: number[] = [];
    for (const i of items) {
      values.push(i.value);
      if (i.value2 != null) values.push(i.value2);
    }
    const min = Math.min(...values);
    const max = Math.max(...values);
    return { min, max: max === min ? min + 1 : max };
  }

  private projectPoints(
    items: ChartItem[],
    pick: (i: ChartItem) => number,
    range: { min: number; max: number },
  ): SparkPoint[] {
    const rangeV = range.max - range.min || 1;
    const n = items.length;
    return items.map((p, i) => {
      const v = pick(p);
      return {
        x: SPARK_PAD + (i / Math.max(n - 1, 1)) * (SPARK_W - SPARK_PAD * 2),
        y: SPARK_PAD + (1 - (v - range.min) / rangeV) * (SPARK_H - SPARK_PAD * 2),
        label: p.label,
        value: v,
      };
    });
  }

  sparkPoints(points: ChartItem[]): SparkPoint[] {
    if (!points.length) return [];
    return this.projectPoints(points, p => p.value, this.trendRange(points));
  }

  /** The comparison line; null value2 points fall back to value to stay continuous. */
  sparkPoints2(points: ChartItem[]): SparkPoint[] {
    if (!points.length || !this.hasSeries2(points)) return [];
    return this.projectPoints(points, p => p.value2 ?? p.value, this.trendRange(points));
  }

  sparkPolyline(pts: SparkPoint[]): string {
    return pts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  }

  /** SVG polygon points for the area fill under the sparkline. */
  sparkAreaPolygon(pts: SparkPoint[]): string {
    if (pts.length < 2) return '';
    const bottom = (SPARK_H - SPARK_PAD).toFixed(1);
    const firstX = pts[0].x.toFixed(1);
    const lastX = pts[pts.length - 1].x.toFixed(1);
    const linePoints = pts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
    return `${firstX},${bottom} ${linePoints} ${lastX},${bottom}`;
  }

  sparkAriaLabel(title: string | null, points: ChartItem[]): string {
    const parts = [title ?? this.#translate.instant('NOVA_CHART_TREND')];
    if (points.length >= 2) {
      parts.push(this.#translate.instant('NOVA_CHART_TREND_RANGE', {
        from: points[0].value,
        to: points[points.length - 1].value,
      }));
    }
    return parts.join(', ');
  }

  // ---- grouped helpers -----------------------------------------------------

  groupedRows(items: ChartItem[]): GroupedRow[] {
    const max = Math.max(
      ...items.map(i => i.value),
      ...items.map(i => (i.value2 != null ? i.value2 : 0)),
      1,
    );
    return items.map(item => ({
      item,
      pct1: (item.value / max) * 100,
      pct2: item.value2 != null ? (item.value2 / max) * 100 : 0,
    }));
  }

  groupedLabel(index: number): string {
    return this.seriesLabels?.[index] ?? `Series ${index + 1}`;
  }

  // ---- diverging helpers ---------------------------------------------------

  divergingRows(items: ChartItem[]): DivergingRow[] {
    const maxAbs = Math.max(...items.map(i => Math.abs(i.value)), 1);
    return items.map(item => {
      const ratio = (Math.abs(item.value) / maxAbs) * 100;
      const pos = item.value >= 0;
      return {
        item,
        pctPos: pos ? ratio : 0,
        pctNeg: pos ? 0 : ratio,
        positive: pos,
        display: item.display ?? (item.value >= 0 ? `+${item.value}` : `${item.value}`),
      };
    });
  }

  // ---- donut helpers -------------------------------------------------------

  donutSegments(items: ChartItem[]): DonutSegment[] {
    const total = items.reduce((s, i) => s + Math.max(i.value, 0), 0) || 1;
    let cumulative = 0;
    return items.map((item, idx) => {
      const fraction = Math.max(item.value, 0) / total;
      // arc length = fraction * circumference; gap of 1px between segments
      const arc = fraction * DONUT_CIRC - 1;
      const dasharray = `${Math.max(arc, 0).toFixed(2)} ${DONUT_CIRC.toFixed(2)}`;
      // rotate(-90) is applied on the SVG element; offset positions arc after prior segments
      const dashoffset = (-cumulative * DONUT_CIRC).toFixed(2);
      cumulative += fraction;
      return {
        item,
        pct: Math.round(fraction * 100),
        dasharray,
        dashoffset,
        color: DONUT_PALETTE[idx % DONUT_PALETTE.length],
      };
    });
  }

  donutTotal(items: ChartItem[]): number {
    return items.reduce((s, i) => s + Math.max(i.value, 0), 0);
  }

  donutItemDisplay(item: ChartItem): string {
    return item.display ?? `${item.value}`;
  }

  donutAriaLabel(title: string | null, items: ChartItem[]): string {
    return `${title ?? this.#translate.instant('NOVA_CHART_COMPOSITION')}: ${items.map(i => `${i.label} ${this.donutItemDisplay(i)}`).join(', ')}`;
  }

  // ---- bullet helpers ------------------------------------------------------

  /**
   * Scale every row to a shared max of all actuals AND targets, with a little
   * headroom so a maxed-out target tick never sits flush against the edge. Each
   * row carries its actual fill %, the target marker %, and an attainment label.
   */
  bulletRows(items: ChartItem[]): BulletRow[] {
    const max =
      Math.max(...items.map(i => i.value), ...items.map(i => i.value2 ?? 0), 1) * 1.08;
    return items.map(item => {
      const target = item.value2;
      if (target == null || target <= 0) {
        return { item, pct: (item.value / max) * 100, targetPct: null, attainment: null, met: false };
      }
      return {
        item,
        pct: (item.value / max) * 100,
        targetPct: (target / max) * 100,
        attainment: `${Math.round((item.value / target) * 100)}%`,
        met: item.value >= target,
      };
    });
  }
}
